import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { TranslateQuestionnaireContent } from '../../shared/TranslateQuestionnaire/TranslateQuestionnaireContent';

import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { LocalizedText } from '@/utils/localization';

import {
    useGetReviewTemplate,
    useUpdateReviewTemplate,
} from '@/api/review-services/review-template';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import {
    createTranslateTitleSchema,
    type TranslateTitleFormData,
} from '@/pages/shared/QuestionnaireCreation/validation';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { validationMessageKey } from '@/utils/validationMessage';

export const TranslateTemplatePage = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();
    const { templateId } = useParams<{ templateId: string }>();

    const { data: template, isLoading } = useGetReviewTemplate(templateId);
    const { mutateAsync: updateTemplate, isPending: isSaving } = useUpdateReviewTemplate();

    const questions = template?.questions || [];

    const [targetLanguage, setTargetLanguage] = useState<string>('');

    const translateTitleSchema = useMemo(
        () => createTranslateTitleSchema(targetLanguage),
        [targetLanguage],
    );

    // TODO: The title input is managed as localTitle in TranslateQuestionnaireContent; useForm here is
    // used only as a validation trigger (setValue → trigger → errors). Intentional — more translatable
    // fields are planned. When added, consider extracting this into a shared useTranslatePage hook.
    const {
        watch,
        setValue,
        reset,
        trigger,
        formState: { errors },
    } = useForm<TranslateTitleFormData>({
        resolver: zodResolver(translateTitleSchema),
        defaultValues: { title: {} },
    });

    const formTitle = watch('title');

    const onTargetLanguageChange = useCallback((language: string) => {
        setTargetLanguage(language);
    }, []);

    useEffect(() => {
        if (targetLanguage) {
            reset({ title: formTitle });
        }
    }, [targetLanguage]);

    const onSave = async (updatedQuestions: ReviewQuestion[], updatedTitle?: LocalizedText) => {
        if (!template) return;

        const titleToSave = updatedTitle ?? template.title;
        setValue('title', titleToSave);

        const isValid = await trigger('title');

        if (!isValid) {
            const errorMessage = errors.title?.title?.message;
            if (errorMessage) {
                return showWarningToast({ message: t(validationMessageKey(errorMessage)) });
            }
        }

        try {
            await updateTemplate({
                ...template,
                title: titleToSave,
                questions: updatedQuestions,
            });
            showSuccessToast({
                message: t('templateManagement.templateTranslation.message.saveSuccess'),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const onBack = () => {
        navigate({
            path: `${RouteName.TemplateQuestionnaire}/${templateId}`,
            state: { templateId },
        });
    };

    return (
        <TranslateQuestionnaireContent
            questions={questions}
            isLoading={isLoading}
            isSaving={isSaving}
            onSave={onSave}
            onBack={onBack}
            backLabel={t('common.backToTemplate')}
            title={t('templateManagement.templateTranslation.title')}
            subtitle={t('templateManagement.templateTranslation.subtitle')}
            evaluationTitle={template?.title}
            titleError={errors.title?.title?.message as string | undefined}
            onTargetLanguageChange={onTargetLanguageChange}
        />
    );
};
